import {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocalizer} from '@/common/services/localizer';
import {StoreThunkDispatch} from '@/app/store';
import {selectDeviceCoordinates} from '@/app/store/geolocation/selectors';
import {determinateExactSettlement} from '@/app/store/geolocation/actions';

const ignoreFlagLsKey = 'sinoptik:ignoreGeolocationMismatchCheck';

/**
 * The hook contains the logic for determining the difference between the server definition of the location and
 * the client (winch using the geolocation of the device)
 * to display the confirmation window for the navigating to the location determined by the geolocation.
 */
export function useGeolocationMismatchCheck({
	isMainPage,
	currentLocationId,
}: {
	isMainPage: boolean;
	currentLocationId: string;
}) {
	const deviceCoordinates = useSelector(selectDeviceCoordinates);
	const {language} = useLocalizer();

	const [geolocationMismatched, showGeolocationMismatched] = useState(false);

	const dispatch = useDispatch<StoreThunkDispatch>();

	const handleMismatchSkip = useCallback(async () => {
		showGeolocationMismatched(false);
	}, []);

	const handleMismatchRefuse = useCallback(async () => {
		// Do not ask further during session
		sessionStorage.setItem(ignoreFlagLsKey, '1');
		showGeolocationMismatched(false);
	}, []);

	// Check is current location was determined correct by compare with a determined by geolocation one
	useEffect(() => {
		if (!deviceCoordinates || !isMainPage || sessionStorage.getItem(ignoreFlagLsKey)) {
			return;
		}

		dispatch(determinateExactSettlement({language})).then(result => {
			if (result.payload && result.payload.location.id !== currentLocationId) {
				showGeolocationMismatched(true);
			}
		});
	}, [deviceCoordinates]);

	return {
		geolocationMismatched,
		handleMismatchSkip,
		handleMismatchRefuse,
	};
}

useGeolocationMismatchCheck.displayName = 'useGeolocationMismatchCheck';
